import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { patchPlatformTenant, type PlatformTenantRow, type PlatformTenantSort } from '@/api/platformApi'
import { getPlatformTenantDetailPath } from '@/config/platform'
import { usePlatformTenantsList } from '@/pages/Platform/usePlatformTenantsList'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { PlatformExportActions } from '@/pages/Platform/PlatformExportActions'
import styles from './Platform.module.css'

const PAGE_SIZE = 20

function formatQuota(used: number, limit: number | null | undefined) {
  if (limit == null) return `${used} / ∞`
  return `${used} / ${limit}`
}

function formatDateShort(iso: string | null | undefined) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}

function tenantInitials(row: PlatformTenantRow) {
  const base = (row.nome || row.slug || '?').trim()
  const parts = base.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return base.slice(0, 2).toUpperCase()
}

function healthLevel(days: number): 'ok' | 'warn' | 'critical' {
  if (days >= 60) return 'critical'
  if (days >= 30) return 'warn'
  return 'ok'
}

function healthMetricClass(days: number): string {
  const level = healthLevel(days)
  if (level === 'critical') return styles.tenantMetricHealthCritical
  if (level === 'warn') return styles.tenantMetricHealthWarn
  return ''
}

function TenantListCard({
  row,
  onToggle,
}: {
  row: PlatformTenantRow
  onToggle: (row: PlatformTenantRow) => void
}) {
  const health = healthLevel(row.dias_sem_actividade ?? 0)
  const quotaIssue = row.quota_usuarios_excedida || row.quota_agendamentos_mes_excedida

  return (
    <article
      className={`${styles.tenantListCard} ${!row.ativo ? styles.tenantListCardOff : ''} ${quotaIssue ? styles.tenantListCardWarn : ''}`}
    >
      <Link to={getPlatformTenantDetailPath(row.id)} className={styles.tenantListCardMain}>
        <span className={styles.tenantListAvatar} aria-hidden>
          {tenantInitials(row)}
        </span>
        <div className={styles.tenantListIdentity}>
          <div className={styles.tenantListSlugRow}>
            <code className={styles.tenantListSlug}>{row.slug}</code>
            {row.plano ? <span className={styles.tenantListPlan}>{row.plano}</span> : null}
          </div>
          <p className={styles.tenantListName}>{row.nome || '—'}</p>
        </div>
      </Link>

      <div className={styles.tenantListMetrics}>
        <div
          className={`${styles.tenantMetric} ${row.quota_usuarios_excedida ? styles.tenantMetricWarn : ''}`}
          title="Utilizadores"
        >
          <span className={styles.tenantMetricLabel}>Utilizadores</span>
          <span className={styles.tenantMetricValue}>
            {formatQuota(row.usuarios_total, row.limite_usuarios)}
          </span>
        </div>
        <div
          className={`${styles.tenantMetric} ${row.quota_agendamentos_mes_excedida ? styles.tenantMetricWarn : ''}`}
          title="Agendamentos do mês"
        >
          <span className={styles.tenantMetricLabel}>Ag. mês</span>
          <span className={styles.tenantMetricValue}>
            {formatQuota(row.agendamentos_mes_atual ?? 0, row.limite_agendamentos_mes)}
          </span>
        </div>
        <div className={`${styles.tenantMetric} ${healthMetricClass(row.dias_sem_actividade ?? 0)}`}>
          <span className={styles.tenantMetricLabel}>Último ag.</span>
          <span className={styles.tenantMetricValue}>{formatDateShort(row.ultimo_agendamento_em)}</span>
          {health !== 'ok' && (
            <span className={styles.tenantMetricSub}>{row.dias_sem_actividade}d inactiva</span>
          )}
        </div>
      </div>

      <div className={styles.tenantListAside}>
        {row.ativo ? (
          <span className={styles.badgePillOn}>Activa</span>
        ) : (
          <span className={styles.badgePillOff}>Suspensa</span>
        )}
        {quotaIssue && <span className={styles.badgeWarn}>Quota</span>}
        <div className={styles.tenantListActions}>
          <Link
            className={styles.tenantActionBtn}
            to={`${getPlatformTenantDetailPath(row.id)}?edit=1`}
            title="Editar"
          >
            Editar
          </Link>
          <Link className={styles.tenantActionBtn} to={getPlatformTenantDetailPath(row.id)} title="Ver detalhe">
            Ver
          </Link>
          <button
            type="button"
            className={`${styles.tenantActionBtn} ${row.ativo ? styles.tenantActionDanger : styles.tenantActionPrimary}`}
            onClick={() => onToggle(row)}
          >
            {row.ativo ? 'Suspender' : 'Reactivar'}
          </button>
        </div>
      </div>
    </article>
  )
}

export function PlatformTenantsPage() {
  const [q, setQ] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [ativo, setAtivo] = useState<'all' | '1' | '0'>('all')
  const [sort, setSort] = useState<PlatformTenantSort>('id')
  const [page, setPage] = useState(1)
  const [acaoErro, setAcaoErro] = useState('')
  const { notifySaved, notifyError } = useSaveFeedback()

  const listParams = useMemo(
    () => ({ q, ativo, page, limit: PAGE_SIZE, sort }),
    [q, ativo, page, sort]
  )
  const { rows, total, pages, loading, erro, reload } = usePlatformTenantsList(listParams)

  const pageStats = useMemo(
    () => ({
      activas: rows.filter((r) => r.ativo).length,
      suspensas: rows.filter((r) => !r.ativo).length,
      quotaIssues: rows.filter((r) => r.quota_usuarios_excedida || r.quota_agendamentos_mes_excedida).length,
      inactive: rows.filter((r) => (r.dias_sem_actividade ?? 0) >= 30).length,
    }),
    [rows]
  )

  const applySearch = () => {
    setPage(1)
    setQ(searchInput.trim())
  }

  const toggleAtivo = useCallback(
    async (row: PlatformTenantRow) => {
      setAcaoErro('')
      const next = !row.ativo
      try {
        await patchPlatformTenant(row.id, { ativo: next })
        await reload()
        notifySaved(next ? 'Conta activada.' : 'Conta suspensa.')
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Erro ao actualizar.'
        setAcaoErro(msg)
        notifyError(e, msg)
      }
    },
    [reload, notifySaved, notifyError]
  )

  const hasFilters = q.trim() !== '' || ativo !== 'all'

  return (
    <div className={styles.platPage}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Empresas (contas)</h1>
          <p className={styles.pageLead}>
            Gerir todas as contas da plataforma. Suspenda para bloquear o slug — os dados mantêm-se na base.
          </p>
        </div>
        <PlatformExportActions tenants={rows} stats={null} disabled={loading} />
      </div>

      <section className={styles.tenantListHero} aria-label="Resumo da listagem">
        <div className={styles.tenantListHeroMain}>
          <span className={styles.tenantListHeroCount}>{total.toLocaleString('pt-PT')}</span>
          <span className={styles.tenantListHeroLabel}>
            conta{total !== 1 ? 's' : ''} {hasFilters ? 'encontrada' : 'registada'}
            {total !== 1 && hasFilters ? 's' : ''}
          </span>
        </div>
        <div className={styles.tenantListHeroPills}>
          <span className={styles.tenantListHeroPill}>
            <strong>{pageStats.activas}</strong> activas
          </span>
          <span className={styles.tenantListHeroPill}>
            <strong>{pageStats.suspensas}</strong> suspensas
          </span>
          {pageStats.quotaIssues > 0 && (
            <span className={`${styles.tenantListHeroPill} ${styles.tenantListHeroPillWarn}`}>
              <strong>{pageStats.quotaIssues}</strong> quota excedida
            </span>
          )}
          {pageStats.inactive > 0 && (
            <span className={`${styles.tenantListHeroPill} ${styles.tenantListHeroPillMuted}`}>
              <strong>{pageStats.inactive}</strong> inactivas 30d+
            </span>
          )}
        </div>
        {pages > 1 && (
          <p className={styles.tenantListHeroNote}>Resumo parcial — página {page} de {pages}</p>
        )}
      </section>

      <div className={styles.filterBar}>
        <div className={`${styles.filterField} ${styles.filterFieldGrow}`}>
          <span className={styles.filterLabel}>Pesquisar</span>
          <div className={styles.filterSearchRow}>
            <input
              type="search"
              className={styles.platInput}
              placeholder="Nome ou slug…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              aria-label="Pesquisar nome ou slug"
            />
            <button type="button" className={styles.btnOutline} onClick={applySearch}>
              Pesquisar
            </button>
          </div>
        </div>
        <label className={styles.filterSelectWrap}>
          <span className={styles.filterLabel}>Estado</span>
          <select
            className={styles.platInput}
            value={ativo}
            onChange={(e) => {
              setAtivo(e.target.value as 'all' | '1' | '0')
              setPage(1)
            }}
          >
            <option value="all">Todas</option>
            <option value="1">Activas</option>
            <option value="0">Suspensas</option>
          </select>
        </label>
        <label className={styles.filterSelectWrap}>
          <span className={styles.filterLabel}>Ordenar</span>
          <select
            className={styles.platInput}
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as PlatformTenantSort)
              setPage(1)
            }}
          >
            <option value="id">ID</option>
            <option value="nome">Nome</option>
            <option value="criado_em">Mais recentes</option>
            <option value="ultimo_agendamento">Último agendamento</option>
            <option value="inactividade">Mais inactivas</option>
          </select>
        </label>
      </div>

      {(erro || acaoErro) && (
        <p className={styles.erro} role="alert">
          {erro || acaoErro}
        </p>
      )}

      {loading ? (
        <div className={styles.tenantListSkeleton} aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.tenantListSkeletonRow} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.tenantListEmpty}>
          <span className={styles.tenantListEmptyIcon} aria-hidden>
            ◎
          </span>
          <p className={styles.tenantListEmptyTitle}>Nenhuma conta encontrada</p>
          <p className={styles.tenantListEmptySub}>
            {hasFilters ? 'Tente outros filtros ou limpe a pesquisa.' : 'Crie a primeira conta em Nova conta.'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.tenantListStack}>
            {rows.map((r) => (
              <TenantListCard key={r.id} row={r} onToggle={toggleAtivo} />
            ))}
          </div>

          {pages > 1 && (
            <nav className={styles.pagination} aria-label="Paginação">
              <button
                type="button"
                className={styles.btnOutline}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Anterior
              </button>
              <span className={styles.paginationInfo}>
                Página {page} de {pages}
              </span>
              <button
                type="button"
                className={styles.btnOutline}
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                Seguinte →
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  )
}
